import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Box,
  Typography,
  Button,
  CircularProgress,
  TablePagination,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  CompareArrows as CompareArrowsIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { produtoService, Produto } from '../../services/produto';

const ProdutoTable: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProduto, setSelectedProduto] = useState<Produto | null>(null);

  // Buscar todos os produtos
  const { data: produtos = [], isLoading, error } = useQuery({
    queryKey: ['produtos'],
    queryFn: produtoService.getAll,
  });

  // Filtrar produtos baseado na busca
  const filteredProdutos = useMemo(() => {
    return produtos.filter(produto =>
      produto.nomeProduto.toLowerCase().includes(search.toLowerCase()) ||
      produto.referencia.toLowerCase().includes(search.toLowerCase()) ||
      produto.precoVenda.toString().includes(search.toLowerCase())
    );
  }, [produtos, search]);

  // Paginação dos dados filtrados
  const paginatedProdutos = useMemo(() => {
    return filteredProdutos.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredProdutos, page, rowsPerPage]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = (id: number) => {
    navigate(`/produtos/editar/${id}`);
    handleCloseMenu();
  };

  const handleView = (id: number) => {
    navigate(`/produtos/visualizar/${id}`);
    handleCloseMenu();
  };

  const handleUnificar = (id: number) => {
    navigate(`/produtos/unificar/${id}`);
    handleCloseMenu();
  };

  const handleNew = () => {
    navigate('/produtos/novo');
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, produto: Produto) => {
    setAnchorEl(event.currentTarget);
    setSelectedProduto(produto);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedProduto(null);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography color="error">Erro ao carregar os produtos.</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          placeholder="Filtrar"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            },
          }}
          sx={{ minWidth: 300 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNew}
          sx={{ backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
        >
          Novo Produto
        </Button>
      </Box>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Produto</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Preço Venda</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Sem Comissão</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Qtd Consignada</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Qtd Disponível</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>% Vendas 30 dias</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProdutos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography sx={{ py: 4, color: 'text.secondary' }}>
                    {search ? 'Nenhum produto encontrado para o filtro informado.' : 'Nenhum produto cadastrado.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedProdutos.map((produto) => (
                <TableRow key={produto.id} hover>
                  <TableCell>{produto.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {produto.referencia}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {produto.nomeProduto}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                      {produto.descricao}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {formatCurrency(produto.precoVenda)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={produto.semComissao ? 'Sim' : 'Não'}
                      color={produto.semComissao ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {produto.quantidadeNasPracas || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {produto.quantidadeDisponível || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {produto.porcentagemVendasMesAnterior !== undefined 
                        ? formatPercentage(produto.porcentagemVendasMesAnterior)
                        : 'N/A'
                      }
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={(e) => handleMenuClick(e, produto)}
                      size="small"
                      title="Mais ações"
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleCloseMenu}
                      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      <MenuItem onClick={() => selectedProduto && handleEdit(selectedProduto.id)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EditIcon fontSize="small" />
                          <Typography>Editar</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem onClick={() => selectedProduto && handleView(selectedProduto.id)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <VisibilityIcon fontSize="small" />
                          <Typography>Visualizar</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem onClick={() => selectedProduto && handleUnificar(selectedProduto.id)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CompareArrowsIcon fontSize="small" />
                          <Typography>Unificar</Typography>
                        </Box>
                      </MenuItem>
                    </Menu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredProdutos.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} itens`}
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          '.MuiTablePagination-toolbar': {
            pl: 2,
            pr: 2
          }
        }}
      />
    </Paper>
  );
};

export default ProdutoTable;
